import { Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Text } from "react-native";
import { useNavigation } from "@react-navigation/native";
import tw from "twrnc";

import { fetchGuestsCount, updateEventPrivacy } from "../http/events";
import { PrivacySchema } from "../screens/Schemas";
import AutoSave from "./AutoSave";
import {
  hidePageConfirmation,
  isPayPerView,
  privacyWhenShown,
} from "./eventPageVisibility";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import Button from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { TextField } from "@/components/settings/Field";
import {
  ChoiceGroup,
  RowGroup,
  ToggleRow,
} from "@/components/settings/SettingRow";
import {
  SettingsPage,
  SettingsSection,
} from "@/components/settings/SettingsPage";
import { colors } from "@/components/settings/theme";
import { t, tRich } from "@/localization";

const EventPrivacyForm = ({ event }) => {
  const { showActionSheetWithOptions } = useActionSheet();
  const navigation = useNavigation();
  // What the page had when it was hidden on this screen ("Show event page").
  const privacyBeforeHiding = useRef(null);
  const ticketed = isPayPerView(event);
  // The hide warning says how many people lose access to a ticketed event.
  const [guestCount, setGuestCount] = useState(null);
  const countSlug = ticketed && event?.is_published ? event.slug : null;
  useEffect(() => {
    if (!countSlug) return;
    let current = true;
    fetchGuestsCount(countSlug)
      .then((count) => current && setGuestCount(count))
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [countSlug]);

  // Only render the form when event is loaded and has an ID
  if (!event || !event.id) {
    return null;
  }

  const {
    slug,
    event_privacy,
    event_password,
    download_enabled,
    is_test,
    email_pref,
    live_only,
    hide_viewer_count,
    guestbook_enabled,
    prefer_chat,
    is_published,
  } = event;

  return (
    <SettingsPage title={t("Privacy")}>
      <Formik
        initialValues={{
          event_privacy,
          event_password,
          download_enabled: !!download_enabled,
          guestbook_enabled: guestbook_enabled !== false,
          prefer_chat: !!prefer_chat,
          live_only: live_only || false,
          hide_viewer_count: hide_viewer_count || false,
          email_pref: email_pref || "guestbookreminder",
        }}
        onSubmit={async (values) => {
          try {
            await updateEventPrivacy({ slug, ...values });
          } catch (err) {
            throw new Error(t("Could not update privacy settings"));
          }
        }}
        validationSchema={PrivacySchema}
      >
        {(f) => {
          const unpublished = f.values.event_privacy === "unpublished";
          const toggleVisibility = () => {
            if (unpublished) {
              f.setFieldValue(
                "event_privacy",
                privacyWhenShown({
                  ticketed,
                  before: privacyBeforeHiding.current,
                })
              );
              return;
            }
            const { title, message } = hidePageConfirmation({
              ticketed,
              guestCount,
            });
            showActionSheetWithOptions(
              {
                options: [t("Hide event page"), t("Cancel")],
                cancelButtonIndex: 1,
                destructiveButtonIndex: 0,
                title,
                message,
              },
              (buttonIndex) => {
                if (buttonIndex === 0) {
                  privacyBeforeHiding.current = f.values.event_privacy;
                  f.setFieldValue("event_privacy", "unpublished");
                }
              }
            );
          };
          return (
            <>
              <AutoSave {...f} />
              {!is_test && (
                <SettingsSection
                  first
                  title={t("Who can watch")}
                  help={ticketed ? "privacy.ticketed" : "privacy.access"}
                >
                  {ticketed ? (
                    <Callout
                      tone="info"
                      title={t("Ticketed event")}
                      action={
                        <Button
                          text={t("Manage tickets")}
                          icon="card-outline"
                          size="md"
                          fullWidth={false}
                          onPress={() =>
                            navigation.navigate("SellTickets", { event, slug })
                          }
                        />
                      }
                    >
                      {t(
                        "Guests need a ticket to watch. To let some guests in for free, invite them from the Guest List."
                      )}
                    </Callout>
                  ) : (
                    <>
                      <ChoiceGroup
                        accessibilityLabel={t("Who can watch")}
                        value={unpublished ? null : f.values.event_privacy}
                        onChange={(value) =>
                          f.setFieldValue("event_privacy", value)
                        }
                        options={[
                          {
                            value: "unlisted",
                            label: t("Anyone with the link can watch"),
                            description: t(
                              "Share the link; no password or invitation needed."
                            ),
                          },
                          {
                            value: "password_protected",
                            label: t("Password protected"),
                            description: t(
                              "Guests enter a password you give them."
                            ),
                            children: (
                              <TextField
                                label={t("Password")}
                                value={f.values.event_password}
                                onChangeText={f.handleChange("event_password")}
                                onBlur={f.handleBlur("event_password")}
                                placeholder={t("Choose a password")}
                                autoCapitalize="none"
                                autoCorrect={false}
                                error={
                                  f.touched.event_password
                                    ? f.errors.event_password
                                    : null
                                }
                                last
                              />
                            ),
                          },
                          {
                            value: "invite_only",
                            label: t("Invite only"),
                            description: (
                              <>
                                {tRich(
                                  "Only the guests invited by email can join. One device per email at a time. (MUST be on the <list>Guest List</list>)",
                                  {
                                    list: (words) => (
                                      <Text
                                        accessibilityRole="link"
                                        style={{
                                          color: colors.brand,
                                          fontWeight: "600",
                                        }}
                                        onPress={() =>
                                          navigation.navigate("GuestList", {
                                            slug,
                                          })
                                        }
                                      >
                                        {words}
                                      </Text>
                                    ),
                                  }
                                )}
                              </>
                            ),
                          },
                        ]}
                      />
                      <RowGroup style={tw`mt-3`}>
                        <ToggleRow
                          label={t("Ask guests for their name and email")}
                          description={
                            f.values.event_privacy === "invite_only"
                              ? t("Always on for invite-only events.")
                              : t(
                                  "Before they can watch. They'll appear in your Guest List."
                                )
                          }
                          help="privacy.requireDetails"
                          value={
                            f.values.email_pref === "askall" ||
                            f.values.event_privacy === "invite_only"
                          }
                          disabled={f.values.event_privacy === "invite_only"}
                          onValueChange={(value) =>
                            f.setFieldValue(
                              "email_pref",
                              value ? "askall" : "guestbookreminder"
                            )
                          }
                        />
                      </RowGroup>
                    </>
                  )}
                </SettingsSection>
              )}

              <SettingsSection
                first={!!is_test}
                title={t("Replay and download")}
              >
                <RowGroup>
                  <ToggleRow
                    label={t("Live only")}
                    help="privacy.liveOnly"
                    value={!!f.values.live_only}
                    onValueChange={(value) =>
                      f.setFieldValue("live_only", !!value)
                    }
                  />
                  <ToggleRow
                    label={t("Show download link to guests")}
                    help="privacy.download"
                    value={!!f.values.download_enabled}
                    onValueChange={(value) =>
                      f.setFieldValue("download_enabled", value)
                    }
                  />
                  <ToggleRow
                    label={t("Hide viewer counter")}
                    help="privacy.viewerCount"
                    value={!!f.values.hide_viewer_count}
                    onValueChange={(value) =>
                      f.setFieldValue("hide_viewer_count", value)
                    }
                  />
                </RowGroup>
              </SettingsSection>

              {is_published && (
                <SettingsSection
                  title={t("Event page")}
                  help="privacy.hidePage"
                  description={
                    unpublished
                      ? ticketed
                        ? t(
                            "Your event page is hidden. People who bought tickets can't watch until you show it again."
                          )
                        : t(
                            "Your event page is hidden. Guests can't open it until you show it again."
                          )
                      : undefined
                  }
                >
                  <Button
                    text={
                      unpublished ? t("Show event page") : t("Hide event page")
                    }
                    icon={unpublished ? "eye-outline" : "eye-off-outline"}
                    variant={unpublished ? "primary" : "secondary"}
                    size="md"
                    fullWidth={false}
                    onPress={toggleVisibility}
                  />
                </SettingsSection>
              )}
            </>
          );
        }}
      </Formik>
    </SettingsPage>
  );
};

export default EventPrivacyForm;
